import React, { useState, useContext, useEffect, useRef } from "react";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import GroupIcon from "@mui/icons-material/Group";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import MenuIcon from "@mui/icons-material/Menu";
import ArticleIcon from "@mui/icons-material/Article";
import styles from "../Styles/Sidebar.module.css";
import logo from "../assets/logo.png";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getLogout } from "../Services/UserLoginService";
import Loader from "./Loader";
import { ToastContext } from "../utils/ToastContext";
import { setUserDetails } from "../utils/redux/reducer/userSlice";
import { SidebarContext } from "../utils/SidebarContext";
import { setMFAEmail, setShowMFA } from "../utils/redux/reducer/mfaSlice";
import { setDefaultLeadTableColumns } from "../utils/redux/reducer/leadsColumnSlice";
import { Box, Tooltip } from "@mui/material";
import AssignmentIcon from '@mui/icons-material/Assignment';

const drawerWidth = 200;

const ScrollableContainer = styled("div")({
  overflowY: "hidden",
  height: "100vh",
});

const Sidebar = () => {
  const drawerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const userDetails = useSelector(
    (state) => state.user.userDetails || { UserDetails: {} }
  );

  const userPermissions = userDetails?.useraccess || [];

  const [loader, setLoader] = useState(false);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [currentLocation, setCurrentLocation] = useState(pathname);
  const [activeUserMaster, setActiveUserMaster] = useState("");

  const {
    showLeadsNav,
    showSettingsNav,
    toggleShowLeadsNav,
    toggleShowSettingsNav,
    toggleDashboard,
    showAutomationsNav,
    toggleAutomationsNav,
  } = useContext(SidebarContext);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        !isSmallScreen
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSmallScreen]);

  useEffect(() => {
    setCurrentLocation(pathname);
  }, [pathname]);

  const manageLeadsPermissions =
    userPermissions.find((perm) => perm.modulename === "Manage Leads") || {};
  const manageReportsPermissions =
    userPermissions.find((perm) => perm.modulename === "Reports V1") || {};

  const manageUsersPermissions =
    userPermissions.find((perm) => perm.modulename === "Manage Users") || {};
  const manageAutomation =
    userPermissions.find((perm) => perm.modulename === "Automate Templates") ||
    {};

  const managePartnersPermissions =
    userPermissions.find((perm) => perm.modulename === "Partners") || {};

  const manageClientACQPermissions =
    userPermissions.find((perm) => perm.modulename === "Client Acquisition") ||
    {};

  const manageChurnAutomationPermissions =
    userPermissions.find((perm) => perm.modulename === "Churn Automation") ||
    {};

  const deleteLeadPermissions = userPermissions?.find((perm) => perm?.modulename === "Delete Leads") || {};

  const manageTemplatesPermissions = userPermissions.find((perm) => perm.modulename === "Communication Templates") || {};

  //
  const manageLeadDistributionPermissions = userPermissions.find((perm) => perm.modulename === "Lead Distribution") || {};

  const canViewManageLeads = manageLeadsPermissions?.view;
  const canViewManageUsers = manageUsersPermissions?.view;
  const canViewReportsV1 = manageReportsPermissions?.view;
  const canViewPartners = managePartnersPermissions?.view;

  const canViewClientACQ = manageClientACQPermissions?.view;

  const canViewChurnAutomation = manageChurnAutomationPermissions?.view;

  const canViewDeleteLead = deleteLeadPermissions?.view;
  
  const canViewManageTemplates = manageTemplatesPermissions?.view;

  const canViewLeadDistribution = manageLeadDistributionPermissions?.view;

  const manageRequestHistory =
    userPermissions.find((perm) => perm.modulename === "Request History") ||
    {};

  const canViewRequestHistory = manageRequestHistory?.view;

  const canViewManagePermissions =
    userDetails?.UserDetails?.role === "Administrator";
  const canEditAutomation = manageAutomation?.edit;

  const username = userDetails ? userDetails.UserDetails.username : "";

  const navigateDashboard = () => {
    navigate("/dashboard");
    toggleDashboard();
  };

  const handleLogout = async () => {
    if (window.ameyo && window.ameyo.integration) {
      window.ameyo?.integration.doLogout();
      console.log("after logout call");
    } else {
      console.error("Ameyo Integration Script not found");
    }
    localStorage.setItem("ameyoAutoLogin", "true");
    dispatch(setMFAEmail(null));
    dispatch(setShowMFA(false));
    dispatch(setDefaultLeadTableColumns());
    setLoader(true);
    const payload = {
      email: userDetails.UserDetails.email,
    };

    try {
      const data = await getLogout(payload);
      if (data.message === "Logged out successfully" && data.success === true) {
        setLoader(false);
        dispatch(setUserDetails());
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        showToast("Logged out successfully!!!", "success");
        navigate("/login");
      } else {
        setLoader(false);
        showToast("Something went wrong", "error");
      }
    } catch (error) {
      setLoader(false);
    }
  };

  const location = useLocation();
  const isSmartViewActive = useMatch("/smartview/*");
  const isManageLeadsActive = useMatch("/manageLeads");
  const isDeletedLeadsActive = useMatch("/deletedLeads");
  const isLeadActivitiesActive = useMatch("/leadactivities");

  // Determine if any lead-related route is active
  const isLeadSectionActive =
    isSmartViewActive ||
    isManageLeadsActive ||
    isLeadActivitiesActive ||
    ["/smartview", "/manageLeads", "/leadactivities"].includes(
      location.pathname
    );

  const automationLocations = ["/wtemplates", "/psmautomation"];

  const settingLocation = [
    "/lms/manageUsers",
    "/user-permissions",
    "/requesthistory",
  ];

  const handleNavigation = (path, event) => {
    if (event.ctrlKey || event.button === 1) {
      // Ctrl + left click or middle click
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
  };

  const handleV1Click = (path, e) => {
    e.preventDefault();
    setActiveUserMaster("v1");
    navigate(path);
  };

  const handleV2Click = () => {
    setActiveUserMaster("v2");
    window.open(`${process.env.REACT_APP_FE_ORIGIN}/lms/manageUsers`, "_blank");
  };

  return (
    <div>
      {loader && <Loader />}
      <Drawer
        variant={isSmallScreen ? "permanent" : "permanent"}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "block" },
          width: "80px",
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: open ? drawerWidth : 80,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            position: isSmallScreen ? "absolute" : "relative",
          },
        }}
        ref={drawerRef}
      >
        <ScrollableContainer
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: !open ? "center" : "",
            overflowY: "auto",
          }}
        >
          <div className={styles.drawerHeader}>
            <Tooltip title={"Home"} arrow="true" placement="right">
              <img
                src={logo}
                alt="Company Logo"
                className={styles.logo}
                onClick={() => navigate("/dashboard")}
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          </div>
          <Divider />
          <List>
            <Box display="flex" justifyContent="center" alignItems="center">
              <IconButton
                onClick={() => setOpen(!open)}
                className={styles.toggleButton}
              >
                {open ? <DragHandleIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
            <div
              onClick={navigateDashboard}
              className={
                currentLocation === "/dashboard"
                  ? styles.itemWithLogoActive
                  : styles.itemWithLogo
              }
            >
              {open ? (
                <>
                  <SpaceDashboardIcon
                    className={
                      currentLocation === "/dashboard"
                        ? styles.itemIconActive
                        : styles.itemIcon
                    }
                  />
                  <p
                    className={
                      currentLocation === "/dashboard"
                        ? styles.itemTitleActive
                        : styles.itemTitle
                    }
                  >
                    Dashboard
                  </p>
                </>
              ) : (
                <Tooltip title={"Dashboard"} arrow="true" placement="right">
                  <SpaceDashboardIcon
                    className={
                      currentLocation === "/dashboard"
                        ? styles.itemIconActive
                        : styles.itemIcon
                    }
                  />
                </Tooltip>
              )}
            </div>
          </List>
          <Divider />
          <List onClick={() => setOpen(true)}>
            <div className={styles.togglerNavDiv}>
              <Tooltip title={"Leads"} arrow="true" placement="right">
                <div
                  className={
                    isLeadSectionActive
                      ? styles.itemWithLogoActive
                      : styles.itemWithLogo
                  }
                  onClick={toggleShowLeadsNav}
                >
                  <GroupIcon
                    className={
                      isLeadSectionActive
                        ? styles.divTogglerIconActive
                        : styles.divTogglerIcon
                    }
                  />
                  {open ? <p>Leads</p> : null}
                  {open ? (
                    <NavigateNextIcon
                      className={
                        showLeadsNav
                          ? styles.toggleIconRotate
                          : styles.toggleIcon
                      }
                    />
                  ) : null}
                </div>
              </Tooltip>

              {showLeadsNav && open && (
                <div className={styles.subMenu}>
                  <div
                    onClick={(e) => handleNavigation("/smartview/All_Leads", e)}
                    className={
                      isSmartViewActive || location.pathname === "/smartview"
                        ? styles.subMenuItemActive
                        : styles.subMenuItem
                    }
                  >
                    <p>Smart-View</p>
                  </div>

                  {(canViewManageLeads === "1" ||
                    canViewManageLeads === "2") && (
                      <div
                        onClick={(e) => handleNavigation("/manageLeads", e)}
                        className={
                          isManageLeadsActive ||
                            location.pathname === "/manageLeads"
                            ? styles.subMenuItemActive
                            : styles.subMenuItem
                        }
                      >
                        <p>Manage Leads</p>
                      </div>
                    )}
                    {(canViewDeleteLead === "1")  && (
                      <div
                        onClick={(e) => handleNavigation("/deleteLeads", e)}
                        className={
                          isDeletedLeadsActive ||
                            location.pathname === "/deleteLeads"
                            ? styles.subMenuItemActive
                            : styles.subMenuItem
                        }
                      >
                        <p>Delete Leads</p>
                      </div>
                    )}
                </div>
              )}
            </div>
          </List>
          <Divider />
          {(canViewReportsV1 == 1 || canViewReportsV1 == 2) && (
            <List onClick={() => setOpen(true)}>
              <div className={styles.togglerNavDiv}>
                <Tooltip title={"Reports"} arrow="true" placement="right">
                  <div
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_REPORTS_BASE_URL}reports`,
                        "_blank"
                      )
                    }
                    className={styles.divToggler}
                  >
                    <ArticleIcon className={styles.divTogglerIcon} />
                    {open ? (
                      <p className={styles.itemTitle_mdp}>Reports</p>
                    ) : null}
                  </div>
                </Tooltip>
              </div>
            </List>
          )}
          <Divider />
          {/*  Kaizen-B2B(Partner)  */}
          {(canViewPartners == 1 || canViewPartners == 2) && (
            <List onClick={() => setOpen(true)}>
              <div className={styles.togglerNavDiv}>
                <Tooltip title={"Partners"} arrow="true" placement="right">
                  <div
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FE_ORIGIN}/lms/manageleads`,
                        "_blank"
                      )
                    }
                    className={styles.divToggler}
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      height="1em"
                      width="1em"
                      className={styles.divTogglerIcon}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="m11 17 2 2a1 1 0 1 0 3-3"></path>
                      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"></path>
                      <path d="m21 3 1 11h-2"></path>
                      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"></path>
                      <path d="M3 4h8"></path>
                    </svg>
                    {open ? (
                      <p className={styles.itemTitle_mdp}>Partners</p>
                    ) : null}
                  </div>
                </Tooltip>
              </div>
            </List>
          )}
          <Divider />
          {/* Client-ACQ */}
          {(canViewClientACQ == 1 || canViewClientACQ == 2) && (
            <List onClick={() => setOpen(true)}>
              <div className={styles.togglerNavDiv}>
                <Tooltip title={"Client ACQ"} arrow="true" placement="right">
                  <div
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FE_ORIGIN}/lms/clientacquisition`,
                        "_blank"
                      )
                    }
                    className={styles.divToggler}
                  >
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      stroke-width="0"
                      viewBox="0 0 384 512"
                      height="24px"
                      width="24px"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M357.45 468.2c-1.2-7.7-1.3-7.6-9.6-7.6-99.8.2-111.8-2.4-112.7-2.6-12.3-1.7-20.6-10.5-21-23.1-.1-1.6-.2-71.6-1-129.1-.1-4.7 1.6-6.4 5.9-7.5 12.5-3 24.9-6.1 37.3-9.7 4.3-1.3 6.8-.2 8.4 3.5 4.5 10.3 8.8 20.6 13.2 30.9 1.6 3.7.1 4.4-3.4 4.4-10-.2-20-.1-30.4-.1v27h116c-1.4-9.5-2.7-18.1-4-27.5-7 0-13.8.4-20.4-.1-22.6-1.6-18.3-4.4-84-158.6-8.8-20.1-27.9-62.1-36.5-89.2-4.4-14 5.5-25.4 18.9-26.6 18.6-1.7 37.5-1.6 56.2-2 20.6-.4 41.2-.4 61.8-.5 3.1 0 4-1.4 4.3-4.3 1.2-9.8 2.7-19.5 4-29.2.8-5.3 1.6-10.7 2.4-16.1L23.75 0c-3.6 0-5.3 1.1-4.6 5.3 2.2 13.2-.8.8 6.4 45.3 63.4 0 71.8.9 101.8.5 12.3-.2 37 3.5 37.7 22.1.4 11.4-1.1 11.3-32.6 87.4-53.8 129.8-50.7 120.3-67.3 161-1.7 4.1-3.6 5.2-7.6 5.2-8.5-.2-17-.3-25.4.1-1.9.1-5.2 1.8-5.5 3.2-1.5 8.1-2.2 16.3-3.2 24.9h114.3v-27.6c-6.9 0-33.5.4-35.3-2.9 5.3-12.3 10.4-24.4 15.7-36.7 16.3 4 31.9 7.8 47.6 11.7 3.4.9 4.6 3 4.6 6.8-.1 42.9.1 85.9.2 128.8 0 10.2-5.5 19.1-14.9 23.1-6.5 2.7-3.3 3.4-121.4 2.4-5.3 0-7.1 2-7.6 6.8-1.5 12.9-2.9 25.9-5 38.8-.8 5 1.3 5.7 5.3 5.7 183.2.6-30.7 0 337.1 0-2.5-15-4.4-29.4-6.6-43.7zm-174.9-205.7c-13.3-4.2-26.6-8.2-39.9-12.5a44.53 44.53 0 0 1-5.8-2.9c17.2-44.3 34.2-88.1 51.3-132.1 7.5 2.4 7.9-.8 9.4 0 9.3 22.5 18.1 60.1 27 82.8 6.6 16.7 13 33.5 19.7 50.9a35.78 35.78 0 0 1-3.9 2.1c-13.1 3.9-26.4 7.5-39.4 11.7a27.66 27.66 0 0 1-18.4 0z"></path>
                    </svg>
                    {open ? (
                      <p className={styles.itemTitle_mdp}>Client ACQ</p>
                    ) : null}
                  </div>
                </Tooltip>
              </div>
            </List>
          )}
          <Divider />
          {/* Churn-Automation */}
          {(canViewChurnAutomation == 1 || canViewChurnAutomation == 2) && (
            <List onClick={() => setOpen(true)}>
              <div className={styles.togglerNavDiv}>
                <Tooltip
                  title={"Churn Automation"}
                  arrow="true"
                  placement="right"
                >
                  <div
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FE_ORIGIN}/lms/churnautomation`,
                        "_blank"
                      )
                    }
                    className={styles.divToggler}
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      height="24px"
                      width="24px"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M13 20.693c-.905 .628 -2.36 .292 -2.675 -1.01a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.492 .362 1.716 2.219 .674 3.03"></path>
                      <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
                      <path d="M17 22l5 -3l-5 -3z"></path>
                    </svg>
                    {open ? (
                      <p className={styles.itemTitle_mdp}>Churn Automation</p>
                    ) : null}
                  </div>
                </Tooltip>
              </div>
            </List>
          )}
          {/* <Divider /> */}

          <List onClick={() => setOpen(true)}>
            <div className={styles.togglerNavDiv}>
              {
                ((canViewManageUsers === "1" ||
                  canViewManageUsers === "2") || canViewManagePermissions || canViewRequestHistory === "1") && (<>

                    <Tooltip title={"Settings"} arrow="true" placement="right">
                      <div
                        className={
                          settingLocation.includes(currentLocation)
                            ? styles.itemWithLogoActive
                            : styles.itemWithLogo
                        }
                        onClick={toggleShowSettingsNav}
                      >
                        <SettingsIcon
                          className={
                            settingLocation.includes(currentLocation)
                              ? styles.divTogglerIconActive
                              : styles.divTogglerIcon
                          }
                        />
                        {open ? <p>Settings</p> : null}
                        {open ? (
                          <NavigateNextIcon
                            className={
                              showSettingsNav
                                ? styles.toggleIconRotate
                                : styles.toggleIcon
                            }
                          />
                        ) : null}
                      </div>
                    </Tooltip>
                    {showSettingsNav && open && (
                      <div className={styles.subMenu}>
                        {(canViewManageUsers === "1" ||
                          canViewManageUsers === "2") && (
                            <div
                              onClick={(e) => handleV1Click("/manageUsers", e)}
                              className={
                                activeUserMaster === "v1"
                                  ? styles.subMenuItemActive
                                  : styles.subMenuItem
                              }
                            >
                              <p>Manage Users V1</p>
                            </div>
                          )}
                        {(canViewManageUsers === "1" ||
                          canViewManageUsers === "2") && (
                            <div
                              onClick={handleV2Click}
                              className={
                                activeUserMaster === "v2"
                                  ? styles.subMenuItemActive
                                  : styles.subMenuItem
                              }
                            >
                              <p>Manage Users V2</p>
                            </div>
                          )}
                        {canViewManagePermissions && (
                          <div
                            onClick={(e) => handleNavigation("/user-permissions", e)}
                            className={
                              currentLocation === "/user-permissions"
                                ? styles.subMenuItemActive
                                : styles.subMenuItem
                            }
                          >
                            <p>Manage Permissions</p>
                          </div>
                        )}
                        {
                          canViewRequestHistory === "1" && (
                            <div
                              onClick={(e) => handleNavigation("/requesthistory", e)}
                              className={
                                currentLocation === "/requesthistory"
                                  ? styles.subMenuItemActive
                                  : styles.subMenuItem
                              }
                            >
                              <p>Request History</p>
                            </div>
                          )
                        }
                        {/* TODO:- Only ADMIN will able to access this route , based on permissions */}
                        {(canViewManageTemplates === "1") && (
                            <div
                              onClick={(e) => handleNavigation("/comm-template", e)}
                              className={
                                currentLocation === "/comm-template"
                                  ? styles.subMenuItemActive
                                  : styles.subMenuItem
                              }
                            >
                              <p>Comm Templates</p>
                            </div>
                          )
                        }
                        {(canViewLeadDistribution === "1") && (
                            <div
                              onClick={(e) => handleNavigation("/lead-distribution-ratio", e)}
                              className={
                                currentLocation === "/lead-distribution-ratio"
                                  ? styles.subMenuItemActive
                                  : styles.subMenuItem
                              }
                            >
                              <p>Lead Distribution</p>
                            </div>
                          )
                        }
                      </div>
                    )}
                    <Divider />
                  </>)
              }
              {(canEditAutomation === "1" || canEditAutomation === "2") && (
                <div>
                  <List onClick={() => setOpen(true)}>
                    <div className={styles.togglerNavDiv}>
                      <Tooltip
                        title={"Automations"}
                        arrow="true"
                        placement="right"
                      >
                        <div
                          className={
                            automationLocations.includes(currentLocation)
                              ? styles.itemWithLogoActive
                              : styles.itemWithLogo
                          }
                          onClick={toggleAutomationsNav}
                        >
                          <PrecisionManufacturingIcon
                            className={
                              automationLocations.includes(currentLocation)
                                ? styles.divTogglerIconActive
                                : styles.divTogglerIcon
                            }
                          />
                          {open ? <p>Automations</p> : null}
                          {open ? (
                            <NavigateNextIcon
                              className={
                                showAutomationsNav
                                  ? styles.toggleIconRotate
                                  : styles.toggleIcon
                              }
                            />
                          ) : null}
                        </div>
                      </Tooltip>
                      {showAutomationsNav && open && (
                        <div className={styles.subMenu}>
                          <div
                            onClick={(e) => handleNavigation("/wtemplates", e)}
                            className={
                              currentLocation === "/wtemplates"
                                ? styles.subMenuItemActive
                                : styles.subMenuItem
                            }
                          >
                            <p>Whatsapp Automations</p>
                          </div>
                        </div>
                      )}
                      {showAutomationsNav && open && (
                        <div className={styles.subMenu}>
                          <div
                            onClick={(e) =>
                              handleNavigation("/psmautomation", e)
                            }
                            className={
                              currentLocation === "/psmautomation"
                                ? styles.subMenuItemActive
                                : styles.subMenuItem
                            }
                          >
                            <p>PSM Automations</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </List>
                  <Divider />
                </div>
              )}
            </div>
          </List>

          <List>
            <Tooltip
              title={`Logout ${username}`}
              arrow="true"
              placement="right"
            >
              <div className={styles.logoutDiv} onClick={handleLogout}>
                <LogoutIcon cursor="pointer" className={styles.logoutIcon} />
                {open ? <p className={styles.username}>{username}</p> : null}
              </div>
            </Tooltip>
          </List>
        </ScrollableContainer>
      </Drawer>
    </div>
  );
};

export default Sidebar;
